import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DataService } from '../../services/data.service';
import { ProjectGet } from '../../models/project/project-get.model';

export interface DatasourceConflictData {
  datasourceName: string;
  dataSourceId?: string;
  fileContent?: string;
  fileName?: string;
}

export interface DatasourceConflictResult {
  action: 'replace' | 'use-existing' | 'rename' | 'cancel';
  newName?: string;
}

@Component({
  selector: 'app-datasource-name-conflict-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './datasource-name-conflict-dialog.component.html',
  styleUrls: ['./datasource-name-conflict-dialog.component.scss']
})
export class DatasourceNameConflictDialogComponent implements OnInit {
  projectCount: number = 0;
  isLoadingProjects: boolean = true;
  newDatasourceName: string = '';
  showRenameInput: boolean = false;
  projects: ProjectGet[] = [];
  errorMessage: string = '';

  constructor(
    public dialogRef: MatDialogRef<DatasourceNameConflictDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DatasourceConflictData,
    private dataService: DataService
  ) {
    // Initialize new name with the original name
    this.newDatasourceName = data.datasourceName || '';
  }

  ngOnInit(): void {
    this.loadProjectCount();
  }

  loadProjectCount(): void {
    if (!this.data.dataSourceId) {
      this.isLoadingProjects = false;
      return;
    }

    this.dataService.getProjectListByDatasource(this.data.dataSourceId).subscribe({
      next: (projects) => {
        this.projects = projects;
        this.projectCount = projects.length;
        this.isLoadingProjects = false;
      },
      error: (error) => {
        console.error('Error loading project count:', error);
        this.projectCount = 0;
        this.isLoadingProjects = false;
      }
    });
  }

  onReplace(): void {
    this.dialogRef.close({ action: 'replace' } as DatasourceConflictResult);
  }

  onUseExisting(): void {
    this.dialogRef.close({ action: 'use-existing' } as DatasourceConflictResult);
  }

  onShowRenameInput(): void {
    this.showRenameInput = true;
    // Reset error message when showing rename input
    this.errorMessage = '';
  }

  onRename(): void {
    const trimmedName = this.newDatasourceName.trim();
    
    if (!trimmedName) {
      this.errorMessage = 'Data source name is required';
      return;
    }

    if (trimmedName === this.data.datasourceName) {
      this.errorMessage = 'Please enter a different name';
      return;
    }

    // Check if the new name already exists
    this.dataService.checkDataSourceExists(trimmedName).subscribe({
      next: (result) => {
        if (result.exists) {
          this.errorMessage = 'This name is also already in use. Please choose another name.';
        } else {
          this.dialogRef.close({ 
            action: 'rename', 
            newName: trimmedName 
          } as DatasourceConflictResult);
        }
      },
      error: (error) => {
        console.error('Error checking datasource name:', error);
        this.errorMessage = 'Failed to validate name. Please try again.';
      }
    });
  }

  onCancel(): void {
    this.dialogRef.close({ action: 'cancel' } as DatasourceConflictResult);
  }

  onCancelRename(): void {
    this.showRenameInput = false;
    this.newDatasourceName = this.data.datasourceName || '';
    this.errorMessage = '';
  }
}
